import { TeamManagementSummaryFragment } from '@admin/schema';

export type TeamSortKey = 'name' | 'geo' | 'region' | 'members' | 'assignable';

export type TeamSortDirection = 'asc' | 'desc';

export type TeamSort = {
  key: TeamSortKey;
  direction: TeamSortDirection;
};

export const DEFAULT_TEAM_SORT: TeamSort = {
  key: 'name',
  direction: 'asc',
};

const compareStrings = (left: string, right: string) => left.localeCompare(right, undefined, { sensitivity: 'base' });

const compareAssignableLast = (left: TeamManagementSummaryFragment, right: TeamManagementSummaryFragment) => {
  if (left.assignable === right.assignable) {
    return 0;
  }

  return left.assignable ? -1 : 1;
};

export const sortTeams = (
  teams: TeamManagementSummaryFragment[],
  { key, direction }: TeamSort,
): TeamManagementSummaryFragment[] => {
  const multiplier = direction === 'asc' ? 1 : -1;

  return [...teams].sort((left, right) => {
    let result = 0;

    switch (key) {
      case 'name':
        if (direction === 'asc') {
          const assignableOrder = compareAssignableLast(left, right);
          if (assignableOrder !== 0) {
            return assignableOrder;
          }
        }
        result = compareStrings(left.name, right.name);
        break;
      case 'geo':
        result = compareStrings(left.geo ?? '', right.geo ?? '');
        break;
      case 'region':
        result = compareStrings(left.region?.name ?? '', right.region?.name ?? '');
        break;
      case 'members':
        result = left.usersCount - right.usersCount;
        break;
      case 'assignable':
        result = Number(left.assignable) - Number(right.assignable);
        break;
      default:
        result = 0;
    }

    if (result !== 0) {
      return result * multiplier;
    }

    if (key !== 'assignable') {
      const assignableOrder = compareAssignableLast(left, right);
      if (assignableOrder !== 0) {
        return assignableOrder;
      }
    }

    return compareStrings(left.name, right.name);
  });
};

export const teamSortLabel: Record<TeamSortKey, string> = {
  name: 'Name',
  geo: 'Geo',
  region: 'Region',
  members: 'Members',
  assignable: 'Assignable',
};
